import { isDefined } from "@clipboard-health/util-ts";
import { type BreakPolicy } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes } from "date-fns";
import { useMemo } from "react";

import { formatDurationWithBreakParts } from "../../utils/formatDurationWithBreakParts";

interface UseShiftDurationWithBreakParams {
  startDate: Date;
  endDate: Date;
  requiresLunchBreak?: boolean;
  breakPolicy?: BreakPolicy;
  minutesElapsed?: number;
}

interface UseShiftDurationWithBreakResult {
  totalDurationInHours: number;
  breakDurationInMinutes: number;
  requiresBreak: boolean;
  durationParts: { duration: string; breakInfo: string };
  mandatoryBreakDurationInMinutes: number;
}

export function useShiftDurationWithBreak(
  params: UseShiftDurationWithBreakParams
): UseShiftDurationWithBreakResult {
  const { startDate, endDate, requiresLunchBreak = false, breakPolicy, minutesElapsed } = params;

  return useMemo(() => {
    const totalDurationInHours = differenceInMinutes(endDate, startDate) / 60;
    const requiresBreak = requiresLunchBreak && isDefined(breakPolicy);
    const breakDurationInMinutes = requiresBreak ? breakPolicy.breakDurationInMinutes : 0;
    const durationParts = formatDurationWithBreakParts({
      totalDurationInHours,
      breakDurationInMinutes,
      requiresBreak,
      minutesElapsed,
    });

    return {
      totalDurationInHours,
      breakDurationInMinutes,
      requiresBreak,
      durationParts,
      mandatoryBreakDurationInMinutes: breakDurationInMinutes,
    };
  }, [startDate, endDate, requiresLunchBreak, breakPolicy, minutesElapsed]);
}
